<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { ArrowLeft, Headphones, Share2 } from "@lucide/vue";
import TrackArtwork from "@/components/TrackArtwork.vue";
import { api } from "@/lib/api";
import type { Track } from "@/types";

const route = useRoute();
const track = ref<Track | null>(null);
const recorded = ref(false);
const error = ref("");
const duration = computed(() =>
  track.value
    ? `${Math.floor(track.value.durationMs / 60000)}:${String(Math.floor((track.value.durationMs % 60000) / 1000)).padStart(2, "0")}`
    : "-",
);

onMounted(async () => {
  try {
    track.value = (await api.publicTrack(String(route.params.id))).track;
  } catch {
    error.value = "暂时无法加载这首作品。";
  }
});

async function recordPlay() {
  if (!track.value || recorded.value) return;
  recorded.value = true;
  try {
    const result = await api.recordPlay(track.value.id);
    track.value.plays = result.plays;
  } catch {
    // 播放不应因统计失败而中断。
  }
}

async function share() {
  const data = { title: track.value?.title || "声浪工坊作品", url: location.href };
  if (navigator.share) await navigator.share(data);
  else {
    await navigator.clipboard.writeText(location.href);
    alert("链接已复制");
  }
}
</script>

<template>
  <main class="m-page">
    <header class="m-header">
      <span class="brand-mark"><Headphones :size="19" /></span>
      <div>
        <strong>声浪工坊</strong>
        <small>公开试听</small>
      </div>
      <router-link class="m-back" to="/m"><ArrowLeft :size="16" />去创作</router-link>
    </header>

    <section v-if="track" class="m-result">
      <TrackArtwork :title="track.title" />
      <p class="eyebrow">NEW INDEPENDENT RELEASE</p>
      <h1>{{ track.title }}</h1>
      <p class="m-result-meta">{{ track.artist }} · {{ track.genre }} · {{ duration }}</p>
      <audio :src="track.audioUrl" controls preload="metadata" @play.once="recordPlay" />
      <div class="m-result-actions">
        <button class="m-button m-button-lime" type="button" @click="share">
          <Share2 :size="17" />分享这首歌
        </button>
      </div>
      <div class="m-story">
        <span>ABOUT THIS TRACK</span>
        <p>{{ track.prompt }}</p>
      </div>
      <p class="m-plays"><Headphones :size="15" />{{ track.plays }} 次公开试听</p>
    </section>
    <section v-else class="m-result">
      <h1>{{ error || "正在加载作品…" }}</h1>
      <router-link class="m-button m-button-lime" to="/m">返回创作</router-link>
    </section>

    <footer class="m-footer">由声浪工坊 × MiniMax Music 3.0 创作 · AI 辅助音乐作品</footer>
  </main>
</template>
